import {
  AccountData,
  CourseOrderRow,
  DetailedPeriod,
  EventOrderRow,
  ExtendedDashboardOut,
  OrderPageData,
  PartialLabelData,
  PassRow,
  PaymentLinkRow,
  PrivateOrderRow,
  RecurringPassChargeRow,
  RefundRow,
  SavedCardChargeRow,
  VoucherRow,
} from '../../../api';
import { getOrderPage, getPassOrderPage } from '../../../utils/AccountUtils';
import { DETAILED_PERIODS, PeriodKey, SectionType } from '../sectionMeta';
import { UnifiedRow, buildPersonName, extractDiscount } from './UnifiedRow';

type Resolved = { orderPageLabel?: PartialLabelData; locationId?: string };

const resolveByOrderPage = (account: AccountData, orderPageId?: string): Resolved => {
  if (!orderPageId) return {};
  const op = getOrderPage(account, orderPageId) as OrderPageData | undefined;
  if (!op) return {};
  return { orderPageLabel: op.label, locationId: op.location_id };
};

const resolveByPassOrderPage = (account: AccountData, passOrderPageId?: string): Resolved => {
  if (!passOrderPageId) return {};
  const pass = getPassOrderPage(account, passOrderPageId);
  if (!pass) return {};
  const firstRedeemable = pass.redeemable_on?.[0]?.order_page_id;
  const redeemableOp = firstRedeemable
    ? (getOrderPage(account, firstRedeemable) as OrderPageData | undefined)
    : undefined;
  return { orderPageLabel: pass.label, locationId: redeemableOp?.location_id };
};

const mapEvent = (r: EventOrderRow, period: PeriodKey, account: AccountData): UnifiedRow => {
  const { orderPageLabel, locationId } = resolveByOrderPage(account, r.order_page_id);
  return {
    key: `${SectionType.EVENTS}:${r.id}:${period}`,
    sectionType: SectionType.EVENTS,
    period,
    entityId: r.id,
    orderNumber: r.order_number,
    orderPageId: r.order_page_id,
    orderPageLabel,
    locationId,
    personName: buildPersonName(r.firstname, r.lastname),
    persons: r.persons,
    price: r.price,
    date: r.created_at,
    discount: extractDiscount(r),
    raw: r,
  };
};

const mapPrivate = (r: PrivateOrderRow, period: PeriodKey, account: AccountData): UnifiedRow => {
  const { orderPageLabel, locationId } = resolveByOrderPage(account, r.order_page_id);
  return {
    key: `${SectionType.PRIVATE_SESSIONS}:${r.id}:${period}`,
    sectionType: SectionType.PRIVATE_SESSIONS,
    period,
    entityId: r.id,
    orderNumber: r.order_number,
    orderPageId: r.order_page_id,
    orderPageLabel,
    locationId,
    personName: buildPersonName(r.firstname, r.lastname),
    persons: r.persons,
    price: r.price,
    date: r.created_at,
    discount: extractDiscount(r),
    raw: r,
  };
};

const mapCourse = (r: CourseOrderRow, period: PeriodKey, account: AccountData): UnifiedRow => {
  const { orderPageLabel, locationId } = resolveByOrderPage(account, r.order_page_id);
  return {
    key: `${SectionType.COURSES}:${r.id}:${period}`,
    sectionType: SectionType.COURSES,
    period,
    entityId: r.id,
    orderNumber: r.order_number,
    orderPageId: r.order_page_id,
    orderPageLabel,
    locationId,
    personName: buildPersonName(r.firstname, r.lastname),
    persons: r.persons,
    price: r.price,
    date: r.created_at,
    discount: extractDiscount(r),
    raw: r,
  };
};

const mapVoucher = (r: VoucherRow, period: PeriodKey, account: AccountData): UnifiedRow => {
  const { orderPageLabel, locationId } = resolveByOrderPage(account, r.order_page_id);
  return {
    key: `${SectionType.VOUCHERS}:${r.id}:${period}`,
    sectionType: SectionType.VOUCHERS,
    period,
    entityId: r.id,
    orderNumber: r.order_number,
    orderPageId: r.order_page_id,
    orderPageLabel,
    locationId,
    code: r.code,
    personName: buildPersonName(r.firstname, r.lastname),
    persons: r.persons,
    price: r.price,
    date: r.created_at,
    discount: extractDiscount(r),
    raw: r,
  };
};

const mapPass =
  (sectionType: SectionType.PASSES | SectionType.RECURRING_PASSES) =>
  (r: PassRow, period: PeriodKey, account: AccountData): UnifiedRow => {
    const { orderPageLabel, locationId } = resolveByPassOrderPage(account, r.pass_order_page_id);
    return {
      key: `${sectionType}:${r.id}:${period}`,
      sectionType,
      period,
      entityId: r.id,
      orderNumber: r.order_number,
      orderPageId: r.pass_order_page_id,
      orderPageLabel,
      locationId,
      code: r.code,
      personName: buildPersonName(r.firstname, r.lastname),
      persons: 1,
      price: r.price,
      date: r.created_at,
      discount: extractDiscount(r),
      raw: r,
    };
  };

const mapRecurringCharge = (r: RecurringPassChargeRow, period: PeriodKey, account: AccountData): UnifiedRow => {
  const { orderPageLabel, locationId } = resolveByPassOrderPage(account, r.pass_order_page_id);
  return {
    key: `${SectionType.RECURRING_PASS_CHARGES}:${r.pass_id}:${r.paid_at}:${period}`,
    sectionType: SectionType.RECURRING_PASS_CHARGES,
    period,
    entityId: r.pass_id,
    orderPageId: r.pass_order_page_id,
    orderPageLabel,
    locationId,
    code: r.code,
    personName: buildPersonName(r.firstname, r.lastname),
    persons: 1,
    price: r.price,
    date: r.paid_at,
    discount: extractDiscount(r),
    raw: r,
  };
};

const mapPaymentLink = (r: PaymentLinkRow, period: PeriodKey, account: AccountData): UnifiedRow => {
  const { orderPageLabel, locationId } = resolveByOrderPage(account, r.order_page_id);
  return {
    key: `${SectionType.PAYMENT_LINKS}:${r.id}:${period}`,
    sectionType: SectionType.PAYMENT_LINKS,
    period,
    entityId: r.order_id,
    orderNumber: r.order_number,
    orderPageId: r.order_page_id,
    orderPageLabel,
    locationId,
    personName: buildPersonName(r.firstname, r.lastname),
    price: r.price,
    date: r.paid_at,
    discount: extractDiscount(r),
    raw: r,
  };
};

const mapChargedLine =
  (sectionType: SectionType.SAVED_CARD_CHARGES | SectionType.POS_CHARGES) =>
  (r: SavedCardChargeRow, period: PeriodKey, account: AccountData): UnifiedRow => {
    const { orderPageLabel, locationId } = resolveByOrderPage(account, r.order_page_id);
    return {
      key: `${sectionType}:${r.id}:${period}`,
      sectionType,
      period,
      entityId: r.order_id,
      orderNumber: r.order_number,
      orderPageId: r.order_page_id,
      orderPageLabel,
      locationId,
      personName: buildPersonName(r.firstname, r.lastname),
      price: r.price,
      date: r.paid_at,
      discount: extractDiscount(r),
      raw: r,
    };
  };

const mapRefund = (r: RefundRow, period: PeriodKey, account: AccountData): UnifiedRow => {
  const { orderPageLabel, locationId } = resolveByOrderPage(account, r.order_page_id);
  return {
    key: `${SectionType.REFUNDS}:${r.id}:${period}`,
    sectionType: SectionType.REFUNDS,
    period,
    entityId: r.owner_id,
    orderNumber: r.order_number,
    orderPageId: r.order_page_id,
    orderPageLabel,
    locationId,
    personName: buildPersonName(r.firstname, r.lastname),
    price: r.price,
    date: r.paid_at,
    discount: extractDiscount(r),
    raw: r,
  };
};

const flattenPeriod = (period: PeriodKey, data: DetailedPeriod | undefined, account: AccountData): UnifiedRow[] => {
  if (!data) return [];
  return [
    ...(data.events?.rows ?? []).map((r) => mapEvent(r, period, account)),
    ...(data.private_sessions?.rows ?? []).map((r) => mapPrivate(r, period, account)),
    ...(data.courses?.rows ?? []).map((r) => mapCourse(r, period, account)),
    ...(data.vouchers?.rows ?? []).map((r) => mapVoucher(r, period, account)),
    ...(data.passes?.rows ?? []).map((r) => mapPass(SectionType.PASSES)(r, period, account)),
    ...(data.recurring_passes?.rows ?? []).map((r) => mapPass(SectionType.RECURRING_PASSES)(r, period, account)),
    ...(data.recurring_pass_charges?.rows ?? []).map((r) => mapRecurringCharge(r, period, account)),
    ...(data.payment_links?.rows ?? []).map((r) => mapPaymentLink(r, period, account)),
    ...(data.saved_card_charges?.rows ?? []).map((r) =>
      mapChargedLine(SectionType.SAVED_CARD_CHARGES)(r, period, account),
    ),
    ...(data.pos_charges?.rows ?? []).map((r) => mapChargedLine(SectionType.POS_CHARGES)(r, period, account)),
    ...(data.refunds?.rows ?? []).map((r) => mapRefund(r, period, account)),
  ];
};

export const flattenDetailedPeriods = (data: ExtendedDashboardOut | undefined, account: AccountData): UnifiedRow[] => {
  if (!data) return [];
  return DETAILED_PERIODS.flatMap((period) => flattenPeriod(period, (data as any)[period], account)).sort((a, b) =>
    b.date.localeCompare(a.date),
  );
};

export type SectionCounts = { count: number; rowsCount: number };

export const getSectionCounts = (data?: ExtendedDashboardOut): Partial<Record<SectionType, SectionCounts>> => {
  const out: Partial<Record<SectionType, SectionCounts>> = {};
  if (!data) return out;
  DETAILED_PERIODS.forEach((period) => {
    const p = (data as any)[period] as DetailedPeriod | undefined;
    if (!p) return;
    const sections: [SectionType, { count?: number; rows?: unknown[] } | undefined][] = [
      [SectionType.EVENTS, p.events],
      [SectionType.PRIVATE_SESSIONS, p.private_sessions],
      [SectionType.COURSES, p.courses],
      [SectionType.VOUCHERS, p.vouchers],
      [SectionType.PASSES, p.passes],
      [SectionType.RECURRING_PASSES, p.recurring_passes],
      [SectionType.RECURRING_PASS_CHARGES, p.recurring_pass_charges],
      [SectionType.PAYMENT_LINKS, p.payment_links],
      [SectionType.SAVED_CARD_CHARGES, p.saved_card_charges],
      [SectionType.POS_CHARGES, p.pos_charges],
      [SectionType.REFUNDS, p.refunds],
    ];
    sections.forEach(([type, section]) => {
      if (!section) return;
      const prev = out[type] ?? { count: 0, rowsCount: 0 };
      out[type] = {
        count: prev.count + (section.count ?? 0),
        rowsCount: prev.rowsCount + (section.rows?.length ?? 0),
      };
    });
  });
  return out;
};
